'use client';
import { create, type StoreApi, type UseBoundStore } from 'zustand';
import { createJSONStorage, persist } from 'zustand/middleware';

import { httpClient } from '@infor/api/clients/base/http-client/http-client';
import type { TAppInforLoginResponse } from '@infor/core/models/auth';
import { storage } from '@infor/utils/src';

import { getMonitoringService } from 'lib/observability/monitoring/monitoring.impl';

import { notificationStore } from './notification/notificationStore';
import {
  createUnidadeSlice,
  type UnidadeSlice,
} from './slices/unidade/unidade-slice';
import { createUserSlice, type UserSlice } from './slices/user/user-slice';
import { safeStateStorage } from './utils/safe-storage';

type SharedState = {
  setLoginData: (data: TAppInforLoginResponse, unidadeLoginId: number) => void;
  setLogoutData: () => void;
  hasHydrated: boolean;
  setHasHydrated: (value: boolean) => void;
};

type GlobalState = UserSlice & UnidadeSlice & SharedState;

export const userStoreBase = create<GlobalState>()(
  persist<GlobalState>(
    (set, get, ...a) => ({
      ...createUserSlice(set, get, ...a),
      ...createUnidadeSlice(set, get, ...a),
      setLoginData: (data: TAppInforLoginResponse, unidadeLoginId: number) => {
        get().setUser(data.usuario);
        get().setPermissoes(data.usuario?.permissoes);
        get().setToken(data.token!);
        get().setUnidades(data.unidades);
        get().setUnidadeCorrente(unidadeLoginId);
        get().setSyncDate(new Date());
      },
      setLogoutData: () => {
        get().setUser(null);
        get().setPermissoes(null);
        get().setToken(null);
        get().setUnidades([]);
        get().setUnidadeCorrente(null);
        get().setSyncDate(null);
      },
      setHasHydrated: (value: boolean) =>
        set((state) => ({ ...state, hasHydrated: value })),
      hasHydrated: false,
    }),
    {
      name: 'user-store',
      storage: createJSONStorage(() => safeStateStorage),
      partialize: (state): GlobalState => {
        const { hasHydrated, ...partialState } = state;

        return partialState as GlobalState;
      },
      onRehydrateStorage: () => {
        return (state, error) => {
          if (error) {
            storage.remove('user-store');
            storage.remove('navigation-history-app-inforcfc');

            getMonitoringService()?.captureError({
              error: error as Error,
              componentName: 'userStore.onRehydrateStorage',
              context: {
                state,
              },
            });

            window.location.href = '/';
          } else {
            state!.hasHydrated = true;

            if (!state) return;

            httpClient.auth.setToken(state.token);
          }
        };
      },
    }
  )
);

const createSelectors = <T extends object>(
  store: UseBoundStore<StoreApi<T>>
) => {
  const useStore = store;

  return {
    useStore,
    selectors: Object.keys(store.getState()).reduce(
      (acc, key) => {
        // eslint-disable-next-line react-hooks/rules-of-hooks
        acc[key as keyof T] = () => useStore((state) => state[key as keyof T]);

        return acc;
      },
      {} as { [K in keyof T]: () => T[K] }
    ),
  };
};

export const useUser = createSelectors(userStoreBase);
export const useNotification = createSelectors(notificationStore);
